import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useUploadWizard } from '../../../contexts/UploadWizardContext';
import { CreativeAssignmentMode } from '../../../types/upload';
import { apiService, licensingAPI } from '../../../services/api';
import { AdMetadata, FileWithStatus } from '../../../types';
import LicenseSelector from '../../common/LicenseSelector';

const ProductionSelectionStep2: React.FC = () => {
  const { state, setProductionId, setActorLicenseIds, setOriginalActorLicenseIds, setAssignmentMode, setIndividualMetadata } = useUploadWizard();
  const [productionInput, setProductionInput] = useState<string>(
    state.productionId?.toString() || ''
  );
  const [assignmentModeLocal, setAssignmentModeLocal] = useState<CreativeAssignmentMode>(state.assignmentMode || CreativeAssignmentMode.NEW);
  const [productionAds, setProductionAds] = useState<any[]>([]);
  const [loadingAds, setLoadingAds] = useState<boolean>(false);
  const [adsError, setAdsError] = useState<string | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [productionExists, setProductionExists] = useState<boolean | null>(null);
  const [checkingProduction, setCheckingProduction] = useState(false);

  interface CreativeOption {
    creativeNumber: number;
    name_en: string;
    video_url?: string;
  }


  const handleProductionIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setProductionInput(value);
    
    // Parse and validate the production ID
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      if (numericValue !== state.productionId) {
        setActorLicenseIds([]);
      }
      setProductionId(numericValue);
    } else if (value === '') {
      setActorLicenseIds([]);
      setProductionId(null);
    }
  };

  const isValid = state.productionId && state.productionId > 0;

  const detectRatio = (file: FileWithStatus): string => {
    if (file.preview?.width && file.preview?.height) {
      const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(file.preview.width, file.preview.height);
      return `${file.preview.width / divisor}:${file.preview.height / divisor}`;
    }
    return '16:9';
  };

  const byCreative = useMemo(() => {
    const map = new Map<number, { ratios: Set<string>; latest: any | null }>();
    productionAds.forEach((ad) => {
      const cn: number = typeof ad.creative_number === 'number' 
        ? ad.creative_number 
        : parseInt(String(ad.creative_number), 10);
      if (!Number.isFinite(cn)) return;
      if (!map.has(cn)) map.set(cn, { ratios: new Set<string>(), latest: null });
      const entry = map.get(cn)!;
      if (ad.ratio) entry.ratios.add(String(ad.ratio));
      if (!entry.latest || new Date(ad.created_at) > new Date(entry.latest.created_at)) {
        entry.latest = ad;
      }
    });
    return map;
  }, [productionAds]);

  const creativeOptions: CreativeOption[] = useMemo(() => {
    const options: CreativeOption[] = [];
    byCreative.forEach((entry, cn) => {
      const latest = entry.latest || {};
      options.push({
        creativeNumber: cn,
        name_en: latest.name_en || `Creative ${cn}`,
        video_url: latest.video_url
      });
    });
    // Sort numerically
    options.sort((a, b) => a.creativeNumber - b.creativeNumber);
    return options;
  }, [byCreative]);

  useEffect(() => {
    // Initialize inputs from any existing metadata
    const initial: Record<string, string> = {};
    state.files.forEach((file) => {
      const md = state.individualMetadata.get(file.id);
      if (md?.creative_number) initial[file.id] = String(md.creative_number);
      else initial[file.id] = '';
    });
    setInputValues(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.files]);

  useEffect(() => {
    // Create object URLs for local previews
    const urls: Record<string, string> = {};
    state.files.forEach((file) => {
      try {
        const url = URL.createObjectURL(file as File);
        urls[file.id] = url;
      } catch {}
    });
    setPreviewUrls(urls);
    return () => {
      Object.values(urls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
    };
  }, [state.files]);

  useEffect(() => {
    // Keep global mode in sync
    setAssignmentMode(assignmentModeLocal);
  }, [assignmentModeLocal, setAssignmentMode]);

  useEffect(() => {
    // Fetch ads when production id is set and mode is EXISTING
    const fetchAds = async () => {
      if (state.productionId && assignmentModeLocal === CreativeAssignmentMode.EXISTING) {
        setLoadingAds(true);
        setAdsError(null);
        try {
          const ads = await apiService.getProductionAds(state.productionId);
          setProductionAds(ads || []);
        } catch (e: any) {
          setAdsError(e.message || 'Failed to load production ads');
          setProductionAds([]);
        } finally {
          setLoadingAds(false);
        }
      }
    };
    fetchAds();
  }, [state.productionId, assignmentModeLocal]);

  // Check if production exists and load its existing actor licenses
  useEffect(() => {
    const checkProduction = async () => {
      if (!state.productionId) {
        setProductionExists(null);
        return;
      }
      setCheckingProduction(true);
      try {
        const ads = await apiService.getProductionAds(state.productionId);
        const exists = ads && ads.length > 0;
        setProductionExists(exists);

        if (exists) {
          // Load existing actor licenses for this production
          const { licenses } = await licensingAPI.getProductionLicenses(String(state.productionId));
          const existingIds = licenses.map(l => l.license_id);
          setOriginalActorLicenseIds(existingIds);
          if (existingIds.length > 0) {
            setActorLicenseIds(existingIds);
          }
        }
      } catch {
        setProductionExists(false);
      } finally {
        setCheckingProduction(false);
      }
    };
    checkProduction();
  }, [state.productionId, setActorLicenseIds]);

  const validateAndSetAssignment = (file: FileWithStatus, creativeNumberInput: string) => {
    setInputValues(prev => ({ ...prev, [file.id]: creativeNumberInput }));

    const parsed = parseInt(creativeNumberInput, 10);
    const ratio = detectRatio(file);

    if (!state.productionId || isNaN(parsed)) {
      // Mark invalid by setting incomplete metadata (creative_number NaN ensures validation fails)
      const invalidMd: AdMetadata = {
        production_id: state.productionId || 0,
        creative_number: Number.NaN as any,
        ratio,
        name_en: '',
        language: 'EN',
        filename: file.name,
        created_by: 'web_user',
        watchers: [],
        duration: file.preview?.duration,
      };
      setIndividualMetadata(file.id, invalidMd);
      return;
    }

    const entry = byCreative.get(parsed);
    const exists = !!entry;
    const ratioFree = exists ? !entry!.ratios.has(ratio) : false;

    if (!exists || !ratioFree) {
      const invalidMd: AdMetadata = {
        production_id: state.productionId,
        creative_number: Number.NaN as any,
        ratio,
        name_en: '',
        language: 'EN',
        filename: file.name,
        created_by: 'web_user',
        watchers: [],
        duration: file.preview?.duration,
      };
      setIndividualMetadata(file.id, invalidMd);
      return;
    }

    // Build valid metadata by copying from latest ad
    const base = entry!.latest;
    const metadata: AdMetadata = {
      production_id: state.productionId,
      creative_number: parsed,
      ratio,
      name_en: base?.name_en || file.name.replace(/\.[^/.]+$/, ''),
      language: base?.language || 'EN',
      filename: file.name,
      created_by: base?.created_by || 'web_user',
      watchers: base?.watchers || [],
      duration: file.preview?.duration,
      tags: base?.tags || (base?.metadata?.tags ?? undefined)
    };
    setIndividualMetadata(file.id, metadata);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Step 2: Production ID
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Enter the production ID for this upload. All files will be associated with this production.
      </Typography>

      {/* Production ID Input */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Production Information
          </Typography>
          
          <TextField
            label="Production ID"
            value={productionInput}
            onChange={handleProductionIdChange}
            type="number"
            fullWidth
            placeholder="e.g., 1337"
            error={productionInput !== '' && !isValid}
            helperText={
              productionInput !== '' && !isValid
                ? 'Please enter a valid production ID (positive number)'
                : 'Enter the numeric production ID for this campaign'
            }
            sx={{ mb: 2 }}
          />

          {isValid && !checkingProduction && productionExists === true && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Production {state.productionId} exists and is ready to use
              </Typography>
            </Alert>
          )}
          {isValid && !checkingProduction && productionExists === false && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Production {state.productionId} is new and will be created on upload
              </Typography>
            </Alert>
          )}
          {isValid && checkingProduction && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Checking production...
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Actor Licenses */}
      {isValid && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Actor Licenses
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {productionExists && state.actorLicenseIds.length > 0
                ? 'This production already has actor licenses linked. You can add or remove as needed.'
                : 'Select actor licenses that apply to this production. Leave empty if none apply.'}
            </Typography>
            <LicenseSelector
              licenseType="actor"
              selectedLicenseIds={state.actorLicenseIds}
              onSelectionChange={setActorLicenseIds}
              label="Actor Licenses"
              placeholder="Search actor licenses..."
            />
          </CardContent>
        </Card>
      )}

      {/* Assignment Mode */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Creative Assignment Mode
          </Typography>
          <RadioGroup
            row
            value={assignmentModeLocal}
            onChange={(e) => setAssignmentModeLocal(e.target.value as CreativeAssignmentMode)}
          >
            <FormControlLabel value={CreativeAssignmentMode.NEW} control={<Radio />} label="New creatives (default)" />
            <FormControlLabel value={CreativeAssignmentMode.EXISTING} control={<Radio />} label="Existing creatives (assign by Creative #)" />
          </RadioGroup>
          <Typography variant="body2" color="text.secondary">
            {assignmentModeLocal === CreativeAssignmentMode.NEW
              ? 'Proceed to assign creative numbers and metadata as usual.'
              : 'Assign each selected file to an existing creative number in this production. We will copy metadata and tags and only add a new ratio.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Existing creatives table */}
      {assignmentModeLocal === CreativeAssignmentMode.EXISTING && state.files.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Assign Files to Existing Creatives
            </Typography>
            {!isValid && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Enter a valid production ID to load existing creatives.
              </Alert>
            )}
            {adsError && (
              <Alert severity="error" sx={{ mb: 2 }}>{adsError}</Alert>
            )}
            {isValid && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Preview</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>Detected Ratio</TableCell>
                      <TableCell>Creative #</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.files.map((file) => {
                      const ratio = detectRatio(file);
                      const input = inputValues[file.id] ?? '';
                      const creativeNumber = input ? parseInt(input, 10) : NaN;
                      const entry = !isNaN(creativeNumber) ? byCreative.get(creativeNumber) : undefined;
                      const exists = !!entry;
                      const ratioFree = exists ? !entry!.ratios.has(ratio) : false;
                      const isRowValid = !!(exists && ratioFree);
                      return (
                        <TableRow key={file.id}>
                          <TableCell>
                            {previewUrls[file.id] ? (
                              (file.type || '').startsWith('image/') ? (
                                <img
                                  src={previewUrls[file.id]}
                                  alt={file.name}
                                  style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4 }}
                                />
                              ) : (
                                <video
                                  src={previewUrls[file.id]}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4 }}
                                />
                              )
                            ) : (
                              <Box sx={{ width: 56, height: 32, bgcolor: 'grey.200', borderRadius: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>{file.name}</TableCell>
                          <TableCell>
                            <Chip label={ratio} size="small" />
                          </TableCell>
                          <TableCell sx={{ minWidth: 320 }}>
                            <Autocomplete
                              size="small"
                              options={creativeOptions}
                              getOptionLabel={(opt) => `${opt.creativeNumber} — ${opt.name_en}`}
                              value={creativeOptions.find(o => String(o.creativeNumber) === input) || null}
                              onChange={(_, opt) => {
                                if (opt) {
                                  const valueStr = String(opt.creativeNumber);
                                  setInputValues(prev => ({ ...prev, [file.id]: valueStr }));
                                  validateAndSetAssignment(file, valueStr);
                                } else {
                                  setInputValues(prev => ({ ...prev, [file.id]: '' }));
                                  validateAndSetAssignment(file, '');
                                }
                              }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Select creative" />
                              )}
                              renderOption={(props, option) => (
                                <li {...props} key={option.creativeNumber}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.video_url ? (
                                      <video
                                        src={option.video_url}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4 }}
                                      />
                                    ) : (
                                      <Box sx={{ width: 56, height: 32, bgcolor: 'grey.200', borderRadius: 1 }} />
                                    )}
                                    <Box>
                                      <Typography variant="body2">{option.name_en}</Typography>
                                      <Typography variant="caption" color="text.secondary">#{option.creativeNumber}</Typography>
                                    </Box>
                                  </Box>
                                </li>
                              )}
                              disabled={!isValid || loadingAds}
                            />
                          </TableCell>
                          <TableCell>
                            {creativeNumber ? (
                              isRowValid ? (
                                <Chip icon={<CheckCircleIcon />} label="Ready" color="success" size="small" />
                              ) : (
                                <Chip icon={<ErrorIcon />} label={
                                  exists ? `Ratio already exists for ${creativeNumber}` : 'Creative not in this production'
                                } color="error" size="small" />
                              )
                            ) : (
                              <Chip label="Awaiting Creative #" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Files Summary */}
      {state.files.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Files to Upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {state.files.length} file(s) selected for Production ID {state.productionId || '[Not Set]'}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {state.files.slice(0, 3).map((file, index) => (
              <Typography key={file.id} variant="caption" display="block" color="text.secondary">
                • {file.name}
              </Typography>
            ))}
            {state.files.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                ... and {state.files.length - 3} more files
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProductionSelectionStep2;